import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Dialog, DialogContent, Button, Alert } from '@mui/material';
import SpeakingTestUI from '../components/IELTS/SpeakingTestUI';
import SpeakingSimulationUI, { isSpeakingSimulationReady } from '../components/IELTS/SpeakingSimulationUI';
import TestModeModal from '../components/IELTS/TestModeModal';
import { hasAIScore, shouldShowScore } from '../components/IELTS/utils/scoreHelpers';

const SpeakingTestPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [testStarted, setTestStarted] = useState(false);
  const [modeModalOpen, setModeModalOpen] = useState(true);
  const [selectedSectionIndices, setSelectedSectionIndices] = useState([0,1,2]);
  const [selectedTimeLimit, setSelectedTimeLimit] = useState(15);
  const [submitted, setSubmitted] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [deliveryMode, setDeliveryMode] = useState('standard');

  const testData = location.state?.testData;
  const testDuration = selectedTimeLimit;
  const currentUserId = location.state?.currentUserId || 1;
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const requiresSubscriptionForAiScoring = !userData?.IsSubscribed;
  const simulationReady = isSpeakingSimulationReady(testData?.sections || []);

  const handleStartPracticeMode = (selectedParts = [1,2,3], timeLimit = 15) => {
    setSelectedSectionIndices(selectedParts.map(p => p - 1));
    setSelectedTimeLimit(timeLimit);
    setDeliveryMode('standard');
    setModeModalOpen(false);
    setTestStarted(true);
  };
  const handleStartSimulationMode = () => {
    setSelectedSectionIndices([0,1,2]);
    setSelectedTimeLimit(15);
    setDeliveryMode(simulationReady ? 'examiner-simulation' : 'standard');
    setModeModalOpen(false);
    setTestStarted(true);
  };

  const handleTestComplete = (results) => {
    setTestResults(results);
    setSubmitted(true);
  };

  if (!testStarted) {
    return (
      <Box>
        <TestModeModal
          open={modeModalOpen}
          onClose={() => {
            setModeModalOpen(false);
            navigate('/ielts-dashboard');
          }}
          onStartPracticeMode={handleStartPracticeMode}
          onStartSimulationMode={handleStartSimulationMode}
          numParts={3}
          defaultTime={15}
          timeOptions={[15, 13, 10]}
          partLabels={["Part 1", "Part 2", "Part 3"]}
          fullPartsLabel="Full test (3 parts - 15 minutes)"
          testCategoryName="Speaking"
          hideSimulationMode={!simulationReady}
          requiresSubscriptionForSimulation={requiresSubscriptionForAiScoring}
        />
      </Box>
    );
  }

  if (!testData) {
    return <Typography variant="h6" color="error">No test data found. Please return to the dashboard and try again.</Typography>;
  }

  if (submitted) {
    const status = testResults?.response?.status;
    const hasAI = testResults ? hasAIScore(testResults) : false;
    const scoreAvailable = testResults ? shouldShowScore(testResults) : false;
    
    // PRIORITY: If AI scoring exists OR score is available, ALWAYS show results - NO DIALOG
    // This handles cases where AI returns results even with low confidence or band 0
    if (hasAI || scoreAvailable) {
      return (
        <SpeakingTestUI
          sections={selectedSectionIndices.map(i => testData.sections[i]).filter(Boolean)}
          onComplete={handleTestComplete}
          testId={testData.id}
          userId={currentUserId}
          apiResult={testResults}
          mode="practice"
          requiresSubscriptionForAiScoring={requiresSubscriptionForAiScoring}
        />
      );
    }
    
    // Only show dialog if NO results are available (pending evaluation with no AI scoring)
    const isPending = status === 'pending_evaluation';
    if (isPending) {
      const attemptId = testResults?.response?.attemptId;
      const pendingResultPath = attemptId
        ? `/ielts-test-details?type=individual&attemptId=${attemptId}`
        : '/ielts-results';

      return (
        <>
          <SpeakingTestUI
            sections={selectedSectionIndices.map(i => testData.sections[i]).filter(Boolean)}
            onComplete={handleTestComplete}
            testId={testData.id}
            userId={currentUserId}
            apiResult={testResults}
            mode="practice"
            requiresSubscriptionForAiScoring={requiresSubscriptionForAiScoring}
          />
          <Dialog open={true} disableEscapeKeyDown={true}>
            <DialogContent>
              <Typography variant="h4" sx={{ color: '#37965E', fontWeight: 700, mb: 3, textAlign: 'center' }}>
                Your speaking test has been submitted.
              </Typography>
              <Typography variant="body1" sx={{ color: '#333', textAlign: 'center', mb: 4 }}>
                Results are pending evaluation. Please check back shortly or visit the results page.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                <Button variant="outlined" color="primary" onClick={() => navigate(pendingResultPath)}>
                  View Result
                </Button>
                <Button variant="contained" color="primary" onClick={() => navigate('/ielts-dashboard')}>
                  Return to Dashboard
                </Button>
              </Box>
            </DialogContent>
          </Dialog>
        </>
      );
    }
    
    // Fallback: show results if status is completed (even without AI scoring)
    const isCompleted = status === 'completed';
    if (isCompleted) {
      return (
        <SpeakingTestUI
          sections={selectedSectionIndices.map(i => testData.sections[i]).filter(Boolean)}
          onComplete={handleTestComplete}
          testId={testData.id}
          userId={currentUserId}
          apiResult={testResults}
          mode="practice"
          requiresSubscriptionForAiScoring={requiresSubscriptionForAiScoring}
        />
      );
    }
  }

  if (deliveryMode === 'examiner-simulation' && simulationReady) {
    return (
      <SpeakingSimulationUI
        sections={selectedSectionIndices.map(i => testData.sections[i]).filter(Boolean)}
        onComplete={handleTestComplete}
        testId={testData.id}
      />
    );
  }

  return (
    <SpeakingTestUI
      sections={selectedSectionIndices.map(i => testData.sections[i]).filter(Boolean)}
      onComplete={handleTestComplete}
      testId={testData.id}
      userId={currentUserId}
      mode="practice"
      requiresSubscriptionForAiScoring={requiresSubscriptionForAiScoring}
    />
  );
};

export default SpeakingTestPage; 
